import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, Inject, ViewContainerRef } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ExperimentTrackingService, RunViewModel } from "../experiment-tracking.service";
import { WaitingService } from "@core/overlays/waiting.service";
import { DataikuAPIService } from "@core/dataiku-api/dataiku-api.service";
import { APIError, catchAPIError, ErrorContext } from "@core/dataiku-api/api-error";
import { BehaviorSubject, map, Observable, ReplaySubject, shareReplay, Subject, switchMap } from "rxjs";
import { ETBreadcrumbItem } from "../experiment-tracking-header/experiment-tracking-header.component";
import { ModalsService, ModalShape } from '@shared/modals/modals.service';
import { ExperimentTrackingRunModelDeployModalComponent, ExperimentTrackingRunModelDeployModalComponentInput } from './experiment-tracking-run-model-deploy-modal/experiment-tracking-run-model-deploy-modal.component';
import { fairAny } from 'dku-frontend-core';
type runTab = 'details' | 'artifacts';

@UntilDestroy()
@Component({
    selector: 'experiment-tracking-run',
    templateUrl: './experiment-tracking-run.component.html',
    styleUrls: ['./experiment-tracking-run.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExperimentTrackingRunComponent implements OnInit, ErrorContext {

    projectKey: string;
    runId: string;
    experimentIds: string[];

    intermediateLinks: Observable<ETBreadcrumbItem>[] = [];

    activeTab: runTab = 'details';
    detailsHref: string;
    artifactsHref: string;
    run$: Observable<RunViewModel>;
    refresh$: Subject<void> = new ReplaySubject(1);
    public error$: BehaviorSubject<APIError | undefined> = new BehaviorSubject<APIError | undefined>(undefined);

    constructor(
        private DataikuAPI: DataikuAPIService,
        private experimentTrackingService: ExperimentTrackingService,
        private waitingService: WaitingService,
        private cd: ChangeDetectorRef,
        private modalsService: ModalsService,
        @Inject('$state') private $state: fairAny,
        private viewContainerRef: ViewContainerRef,
        @Inject('CreateModalFromTemplate') private createModal: fairAny,
        @Inject('$scope') private $scope: fairAny,
        @Inject('$stateParams') private $stateParams: fairAny,
        @Inject('ModelDataUtils') private modelDataUtils: fairAny
    ) {}

    ngOnInit(): void {
        this.projectKey = this.experimentTrackingService.projectKey;
        this.runId = this.experimentTrackingService.runId || '';
        this.experimentIds = this.experimentTrackingService.experimentIds || [];
        this.loadTabs();

        if (this.runId) {
            this.loadRun(this.runId);
        }
        this.refresh();
    }

    loadRun(runId: string) {
        this.run$ = this.refresh$.pipe(
            untilDestroyed(this),
            switchMap(() => {
                return this.DataikuAPI.experiments.getRun(this.projectKey, runId).pipe(
                    this.waitingService.bindSpinner(),
                    map((run) => this.experimentTrackingService.mapRun(run)),
                    catchAPIError(this, false, this.cd));
            }),
            shareReplay()
        );
        this.createBreadcrumb();
    }

    loadTabs() {
        const viewAllRuns = this.experimentTrackingService.viewAllRuns;
        const viewAllExperiments = this.experimentTrackingService.viewAllExperiments;
        const currentUrl = this.experimentTrackingService.currentUrl.split('?')[0];

        this.activeTab = currentUrl.endsWith('details') ? 'details' : 'artifacts';
        this.detailsHref = this.experimentTrackingService.getRunIdHref(this.runId, this.experimentIds, viewAllExperiments, viewAllRuns);
        this.artifactsHref = this.experimentTrackingService.getRunIdArtifactHref(this.runId, this.experimentIds, viewAllExperiments, viewAllRuns);
    }

    createBreadcrumb() {
        const runsListBreadcrumbItem$: Observable<ETBreadcrumbItem> = this.experimentTrackingService.getRunsListBreadcrumbFromRun(this.run$);
        const runNameBreadcrumbItem$: Observable<ETBreadcrumbItem> = this.experimentTrackingService.getRunNameBreadcrumb(this.run$);
        this.intermediateLinks = [runsListBreadcrumbItem$, runNameBreadcrumbItem$];
    }

    hasArtifact(run: RunViewModel) {
        return run.origin != "analysis";
    }

    canDeploy(run: RunViewModel) {
        if(run.origin == 'analysis') {
            return true;
        } else {
            return (run.data?.models?.length || 0) > 0
        } 
    }

    openDeployModal(run: RunViewModel) {
        if (run.origin == 'analysis') {
            this.deployAnalysisModelFromRun(run);
        }
        else {
            this.deployExternalModelFromRun(run);
        }

    }
    deployExternalModelFromRun(run: RunViewModel) {
        this.modalsService.open(
            ExperimentTrackingRunModelDeployModalComponent,
            {
                run: run, projectKey: this.projectKey
            } as ExperimentTrackingRunModelDeployModalComponentInput,
            ModalShape.WIDE,
            this.viewContainerRef,
            { restoreFocus: false }
        ).then(
            () => { this.$state.go("projects.project.flow", { projectKey: this.experimentTrackingService.projectKey }) },
            () => { }
        );
    }

    deployAnalysisModelFromRun(run: RunViewModel) {
        if (run.taskType == "PREDICTION") {
            this.DataikuAPI.analysis.getPredictionModelDetails(run.fullModelId!).pipe(catchAPIError(this)).subscribe((modelDetails)  => {
                this.DataikuAPI.analysis.getCore(this.projectKey, run.analysisId!).pipe(catchAPIError(this)).subscribe((analysisCoreParams)  => {
                    this.$scope.modelData = modelDetails
                    this.$scope.analysisCoreParams = analysisCoreParams;
                    this.$scope.getPredictionType = () => this.modelDataUtils.getPredictionType(modelDetails);
                    this.$scope.getTargetVariable = () => this.modelDataUtils.getTargetVariable(modelDetails);
                    this.$scope.isPartitionedModel = () => this.modelDataUtils.isPartitionedModel(modelDetails);
                    this.$scope.deploymentOrigin = "experiment-tracking-run";
                    this.$stateParams.fullModelId = run.fullModelId;
                    this.createModal("/templates/analysis/prediction/model/deploy-modal.html", this.$scope, "AnalysisPredictionDeployController");
                });
            });
        } else if (run.taskType == "CLUSTERING"){
            this.DataikuAPI.analysis.getClusteringModelDetails(run.fullModelId!).pipe(catchAPIError(this)).subscribe((modelDetails)  => {
                this.DataikuAPI.analysis.getCore(this.projectKey, run.analysisId!).pipe(catchAPIError(this)).subscribe((analysisCoreParams)  => {
                    this.$scope.modelData = modelDetails;
                    this.$scope.analysisCoreParams = analysisCoreParams;
                    this.$scope.getAlgorithm = () => this.modelDataUtils.getAlgorithm(modelDetails)
                    this.$scope.deploymentOrigin = "experiment-tracking-run";
                    this.$stateParams.fullModelId = run.fullModelId;
                    this.createModal("/templates/analysis/clustering/model/deploy-modal.html", this.$scope, "AnalysisClusteringDeployController");
                });
            });
        }
        

    }

    pushError(error?: APIError) {
        this.error$.next(error);
    }

    resetError() {
        this.pushError();
    }

    refresh(): void {
        this.refresh$.next();
    }
}
